import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageToggle from '../../components/LanguageToggle';
import VocationWorksLogo from '../../components/VocationWorksLogo';
import LineIcon from '../../components/LineIcon';

export const NAVY      = '#0D1B2A';
export const AMBER     = '#F5A623';
export const WHITE     = '#FFFFFF';
export const BG        = '#F5F6F8';
export const GREEN     = '#1D9E75';
export const FONT_HEAD = "'Bebas Neue', sans-serif";
export const FONT_BODY = "'DM Sans', sans-serif";

const STEP_LABEL_KEYS = ['worker.signup.stepLabels.account', 'employer.signup.stepLabels.companyInfo'];

export function StepPills({ current }) {
  const { t } = useTranslation();
  return (
    <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.75rem', flexWrap: 'wrap' }}>
      {STEP_LABEL_KEYS.map((lblKey, i) => {
        const lbl = t(lblKey);
        const n      = i + 1;
        const done   = n < current;
        const active = n === current;
        return (
          <div key={i} style={{
            display: 'flex', alignItems: 'center', gap: '6px',
            padding: '5px 12px', borderRadius: '999px',
            fontSize: '0.78rem', fontWeight: 600,
            backgroundColor: active ? AMBER : done ? 'rgba(245,166,35,0.15)' : 'rgba(13,27,42,0.07)',
            color:            active ? NAVY  : done ? AMBER                   : 'rgba(13,27,42,0.35)',
          }}>
            <span style={{
              width: '17px', height: '17px', borderRadius: '50%', flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '0.6rem', fontWeight: 700,
              backgroundColor: active ? NAVY : done ? GREEN : 'rgba(13,27,42,0.12)',
              color:            active ? AMBER: done ? WHITE : 'rgba(13,27,42,0.35)',
            }}>
              {done ? <LineIcon name="check" size={16} /> : n}
            </span>
            {lbl}
          </div>
        );
      })}
    </div>
  );
}

export default function EmployerSignupLayout({
  step,
  tipText,
  onBack,
  backLabel,
  onContinue,
  continueLabel,
  children,
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const resolvedBackLabel     = backLabel     ?? t('worker.signup.backLabel');
  const resolvedContinueLabel = continueLabel ?? t('worker.signup.continueLabel');

  useEffect(() => {
    const link = document.createElement('link');
    link.rel  = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@400;500;600;700&display=swap';
    document.head.appendChild(link);

    const style = document.createElement('style');
    style.textContent = `
      *, *::before, *::after { box-sizing: border-box; }
      body { background-color: ${BG}; font-family: ${FONT_BODY}; color: ${NAVY}; margin: 0; }
      input, select, textarea { font-family: ${FONT_BODY}; }
      input::placeholder, textarea::placeholder { color: rgba(13,27,42,0.32); }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(link);
      document.head.removeChild(style);
    };
  }, []);

  return (
    <div style={{ fontFamily: FONT_BODY, backgroundColor: BG, minHeight: '100dvh', paddingBottom: tipText ? '68px' : '2rem' }}>

      {/* Nav */}
      <nav style={{
        position: 'sticky', top: 0, zIndex: 200, height: '60px',
        backgroundColor: NAVY,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 2rem',
      }}>
        <div onClick={() => navigate('/')} style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
          <VocationWorksLogo variant="reversed" size={36} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <LanguageToggle dark />
          <span style={{ fontSize: '0.875rem', fontWeight: 600, color: AMBER }}>
            {t('common.stepOfTotal', { step, total: 2 })}
          </span>
        </div>
      </nav>

      {/* Progress bar */}
      <div style={{ height: '5px', backgroundColor: 'rgba(13,27,42,0.1)' }}>
        <div style={{
          height: '100%',
          width: `${(step / 2) * 100}%`,
          backgroundColor: AMBER,
          transition: 'width 0.35s ease',
        }} />
      </div>

      {/* Content */}
      <div style={{ maxWidth: '680px', margin: '0 auto', padding: '2.5rem 1.25rem' }}>
        {children}

        {(onBack || onContinue) && (
          <div style={{
            display: 'flex',
            justifyContent: onBack ? 'space-between' : 'flex-end',
            alignItems: 'center',
            marginTop: '1.5rem',
          }}>
            {onBack && (
              <button onClick={onBack} style={{
                background: 'none', border: 'none', padding: 0,
                color: NAVY, fontWeight: 600, fontSize: '0.9rem',
                cursor: 'pointer', fontFamily: FONT_BODY, textDecoration: 'underline',
              }}>
                {resolvedBackLabel}
              </button>
            )}
            {onContinue && (
              <button onClick={onContinue} style={{
                backgroundColor: AMBER, color: NAVY,
                border: 'none', borderRadius: '8px',
                padding: '13px 30px', fontWeight: 700, fontSize: '1rem',
                cursor: 'pointer', fontFamily: FONT_BODY,
              }}>
                {resolvedContinueLabel}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Fixed amber tip banner */}
      {tipText && (
        <div style={{
          position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 150,
          backgroundColor: AMBER, padding: '13px 2rem',
          textAlign: 'center', fontSize: '0.85rem', fontWeight: 500, color: NAVY,
        }}>
          {tipText}
        </div>
      )}
    </div>
  );
}
